/**
 * RoyaleClockPill — a compact mm:ss countdown pill driven by the server's
 * authoritative `endsAt`/`startsAt` epochs (recomputes from the clock every
 * 100ms so it never drifts). Shows tenths in the final 10s and turns red when
 * urgent. Extracted from RoyaleStrip so the redesigned battle screen's
 * who-to-play/clock row and the (spectator) strip share ONE implementation.
 */
import React, {useEffect, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import {FontSizes} from '@/Utils/Dimensions';
import {serverNowMs} from '@/Screens/FunChallenges/util/serverClock';

interface Props {
  endsAt?: number;
  startsAt?: number;
  seconds: number;
  playing: boolean;
}

export default function RoyaleClockPill({
  endsAt,
  startsAt,
  seconds,
  playing,
}: Props) {
  const computeTenths = React.useCallback((): number => {
    // `startsAt`/`endsAt` are the SERVER's epochs, so they must be compared
    // against the server's clock — not this device's. `serverNowMs()` is
    // `Date.now()` corrected by the measured skew, so a machine running 5 min
    // fast no longer computes a negative remainder and slams the pill to 0:00.
    // Falls back to raw Date.now() behaviour when the skew is unmeasured.
    const now = serverNowMs();
    // Before the race starts, freeze at the full duration.
    if (startsAt && now < startsAt) {
      return Math.max(0, Math.round(seconds * 10));
    }
    if (endsAt) {
      return Math.max(0, Math.round((endsAt - now) / 100));
    }
    return Math.max(0, Math.round(seconds * 10));
  }, [endsAt, startsAt, seconds]);

  const [tenths, setTenths] = useState(computeTenths);

  // Re-sync immediately when the authoritative timestamps change.
  useEffect(() => {
    setTenths(computeTenths());
  }, [computeTenths]);

  useEffect(() => {
    if (!playing) {
      // Race is no longer running: settle the display to the authoritative
      // value ONCE. Background tabs throttle setInterval, so if `playing`
      // flipped to false while this tab was hidden the ticking effect was
      // already torn down mid-count and the pill was frozen at a stale value
      // (e.g. "0:03") instead of "0:00". Recomputing here forces the final
      // settle so a returning user always sees the true end state.
      setTenths(computeTenths());
      return;
    }
    const id = setInterval(() => {
      // Recompute from the authoritative epoch (not prev-1) so we never drift.
      setTenths(computeTenths());
    }, 100);
    // Browsers throttle/suspend setInterval in a backgrounded tab, so the pill
    // freezes at its last value while hidden. Re-sync the instant the tab
    // becomes visible or regains focus so a returning user sees the correct
    // remaining time (or 0) immediately, not the stale frozen reading.
    const resync = () => setTenths(computeTenths());
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', resync);
      window.addEventListener('focus', resync);
    }
    return () => {
      clearInterval(id);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', resync);
        window.removeEventListener('focus', resync);
      }
    };
  }, [playing, computeTenths]);

  const totalSec = Math.floor(tenths / 10);
  const mm = Math.floor(totalSec / 60);
  const ss = String(totalSec % 60).padStart(2, '0');
  // Tenths only while time actually remains AND the clock is running — so once
  // the race is over the pill reads a clean "0:00", never "0:00.3".
  const showTenths = playing && totalSec < 10 && tenths > 0;
  const urgent = playing && totalSec < 10 && tenths > 0;
  return (
    <View style={[styles.pill, urgent && styles.pillUrgent]}>
      <CCText style={[styles.text, urgent && styles.textUrgent]}>
        {mm}:{ss}
        {showTenths && <CCText style={styles.tenths}>.{tenths % 10}</CCText>}
      </CCText>
    </View>
  );
}

const styles = StyleSheet.create({
  pill: {
    backgroundColor: '#2A9D8F',
    borderRadius: 8,
    paddingVertical: 8,
    // FIXED width (not minWidth) so the pill doesn't shrink/grow when the
    // tenths digit appears/disappears in the last 10s. Sized to fit "00:00.0".
    width: 96,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pillUrgent: {backgroundColor: '#CD2C39'},
  text: {
    fontSize: FontSizes[18] || 18,
    fontWeight: '800',
    color: '#FFFFFF',
  },
  textUrgent: {color: '#FFFFFF'},
  tenths: {fontSize: FontSizes[13] || 13, fontWeight: '800', color: '#FFFFFF'},
});
